import { addDays, endOfDay, min, startOfDay, startOfToday, subDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { InputProps } from 'reactstrap';

import { DatePickerOptionalProps } from '../../../components/DatePicker/DatePicker';
import { QueueHistoryRangeResult } from '../../../graphql/types';
import { convertFieldsEndOfDateRange } from '../../../utils/dateRange';
import { TWO_WEEKS_DIFFERENCE_FROM_TODAY } from '../constants';
import { FormValues } from '../utils/values';

export interface ForecastRangeDateFieldProps {
  disabled: boolean;
  hasHistoryData: boolean;
  historyRange: QueueHistoryRangeResult;
  queueTimezone: string;
  values: FormValues;
}

export interface RangeInput extends InputProps, DatePickerOptionalProps {}

export const getHistoryStartDateTimeFieldProps = ({
  historyRange: { data },
  values,
  hasHistoryData,
  disabled,
  queueTimezone,
}: ForecastRangeDateFieldProps): RangeInput => {
  let minDate: Date | undefined;

  const historyRange = values?.historyRange;
  const queueId = values?.queueId;

  const startDate = utcToZonedTime(new Date(data?.queueHistoryRange?.startDateTime || 0), queueTimezone);
  const endDate = utcToZonedTime(new Date(data?.queueHistoryRange?.endDateTime || 0), queueTimezone);

  // allow user to choose first whole day within retrieved data
  if (data?.queueHistoryRange?.startDateTime) {
    const startOfStartDateTime = startOfDay(startDate);

    if (startOfStartDateTime.getTime() === startDate.getTime()) {
      minDate = startOfStartDateTime;
    } else {
      minDate = addDays(startOfStartDateTime, 1);
    }
  }

  let maxDate = historyRange?.endDateTime;
  if (!maxDate && data?.queueHistoryRange?.endDateTime) {
    const startOfEndDateTime = startOfDay(endDate);

    if (startOfEndDateTime.getTime() === startDate.getTime()) {
      maxDate = startOfEndDateTime;
    } else {
      maxDate = subDays(startOfEndDateTime, 1);
    }
  }

  const startOfYesterday = subDays(startOfToday(), 1);
  if (maxDate && maxDate > startOfYesterday) {
    maxDate = startOfYesterday;
  }

  const maxSelectableDate = maxDate && subDays(maxDate, TWO_WEEKS_DIFFERENCE_FROM_TODAY);

  return {
    minDate,
    selectableWeekDays:
      !disabled && historyRange?.endDateTime ? [addDays(historyRange.endDateTime, 1).getDay()] : undefined,
    maxDate: maxSelectableDate,
    maxDateRangeOverflow: maxDate,
    disabled: !queueId || !hasHistoryData,
    endDate: historyRange?.endDateTime,
    selectsStart: true,
    startDate: historyRange?.startDateTime,
    type: 'date',
  };
};

export const getHistoryEndDateTimeFieldProps = ({
  historyRange: { data },
  values,
  hasHistoryData,
  disabled,
  queueTimezone,
}: ForecastRangeDateFieldProps): RangeInput => {
  const maxDatePossible = endOfDay(convertFieldsEndOfDateRange(new Date()));
  const historyRange = values?.historyRange;
  const queueId = values?.queueId;

  const startDate = utcToZonedTime(new Date(data?.queueHistoryRange?.startDateTime || 0), queueTimezone);
  const endDate = utcToZonedTime(new Date(data?.queueHistoryRange?.endDateTime || 0), queueTimezone);

  let historyEndDate: Date | undefined;

  if (data?.queueHistoryRange) {
    historyEndDate = startOfDay(endDate);
  }

  const maxDate = historyEndDate ? min([historyEndDate, maxDatePossible]) : undefined;
  let minDate = historyRange?.startDateTime;

  if (!minDate && data?.queueHistoryRange?.startDateTime) {
    const startOfStartDateTime = startOfDay(startDate);

    if (startOfStartDateTime.getTime() === new Date(startDate).getTime()) {
      minDate = startOfStartDateTime;
    } else {
      minDate = addDays(startOfStartDateTime, 1);
    }
  }

  const minSelectableDate = minDate && addDays(minDate, TWO_WEEKS_DIFFERENCE_FROM_TODAY);

  return {
    maxDate,
    selectableWeekDays:
      !disabled && historyRange?.startDateTime ? [subDays(historyRange.startDateTime, 1).getDay()] : undefined,
    minDateRangeOverflow: minDate,
    minDate: minSelectableDate,
    disabled: !queueId || !hasHistoryData,
    endDate: historyRange?.endDateTime,
    selectsEnd: true,
    startDate: historyRange?.startDateTime,
    type: 'date',
  };
};

export const getForecastStartDateTimeFieldProps = ({
  values,
  hasHistoryData,
  disabled,
}: ForecastRangeDateFieldProps): RangeInput => {
  const forecastEndDate = values?.forecastRange?.endDateTime;
  const forecastStartDate = values?.forecastRange?.startDateTime;
  const historyEndDate = values?.historyRange?.endDateTime;
  const historyStartDate = values?.historyRange?.startDateTime;

  return {
    disabled: !historyEndDate || !historyStartDate || !hasHistoryData || disabled,
    endDate: forecastEndDate,
    maxDate: forecastEndDate,
    minDate: startOfToday(),
    selectsStart: true,
    startDate: forecastStartDate,
    type: 'date',
  };
};

export const getForecastEndDateTimeFieldProps = ({
  values,
  hasHistoryData,
  disabled,
}: ForecastRangeDateFieldProps): RangeInput => {
  const forecastEndDate = values?.forecastRange?.endDateTime;
  const forecastStartDate = values?.forecastRange?.startDateTime;
  const historyEndDate = values?.historyRange?.endDateTime;
  const historyStartDate = values?.historyRange?.startDateTime;
  const ONE_DAY_FROM_START_DATE = forecastStartDate || startOfToday();

  return {
    disabled: !historyEndDate || !historyStartDate || !hasHistoryData || disabled,
    endDate: forecastEndDate,
    minDate: ONE_DAY_FROM_START_DATE,
    selectsEnd: true,
    startDate: forecastStartDate,
    type: 'date',
  };
};
